import { useGetTherapistSpecializations } from '_api';
import { AccountType, UserPayload } from '_api/types';
import { ACCOUNT_TYPE } from '_constants';
import {
  InputField,
  SelectField,
  SelectMultipleField,
} from 'components/molecules/formFields';
import React, { useMemo, useState } from 'react';
import { useHospitals, useOrganizations, useUser, useUserForm } from 'utils/hooks';

import { FormBuilder } from './form-builder';
import { BaseFormProps } from './types';

export const UserForm = ({
  disableAll = false,
  hideSubmitButtons = false,
  initValue,
  onBack,
  onSubmit,
}: BaseFormProps<UserPayload>) => {
  const { data: roles, isFetched } = useGetTherapistSpecializations();
  const { hospitalOptions } = useHospitals();
  const { organizationOptions } = useOrganizations();
  const { account } = useUser();
  const { accountRoleOptions } = useUserForm();
  const [newUserRole, setNewUserRole] = useState<AccountType | undefined>(undefined);
  const specializationRoles = useMemo(() => {
    if (roles != undefined) {
      return roles;
    } else return [];
  }, [roles]);

  const update = async (key: any, arg: any) => {
    if (key === 'userRole') {
      setNewUserRole(arg);
    }
  };

  const standardFormFields = [
    {
      component: InputField,
      fieldKey: 'firstName',
      label: 'First Name',
      required: true,
      xs: 4,
    },
    {
      component: InputField,
      fieldKey: 'lastName',
      label: 'Last Name',
      required: true,
      xs: 4,
    },
    {
      component: InputField,
      fieldKey: 'phoneNumber',
      label: 'Phone Number',
      type: 'number',
      mask: '(000) 000-0000',
      xs: 4,
    },
    {
      component: InputField,
      fieldKey: 'email',
      label: 'Email Address',
      required: true,
      type: 'email',
      xs: newUserRole === 'ADMIN' || newUserRole === undefined ? 6 : 4,
    },
    {
      component: SelectField,
      fieldKey: 'userRole',
      label: 'Role',
      options: accountRoleOptions,
      required: true,
      xs:
        newUserRole === 'ADMIN' || newUserRole === undefined
          ? 6
          : newUserRole === 'THERAPIST' &&
              account &&
              account.accountType !== ACCOUNT_TYPE.HOS_ADMIN
            ? 2
            : 4,
    },
  ];

  const orgAdminFormFields = [
    ...standardFormFields,
    {
      component: SelectField,
      fieldKey: 'organizationId',
      label: 'Organization',
      options: organizationOptions,
      disabled: newUserRole !== 'ORGANIZATION_ADMIN',
      required: true,
      xs: 4,
    },
  ];

  const hospitalAdminFormFields = [
    ...standardFormFields,
    {
      component: SelectField,
      fieldKey: 'hospitalId',
      label: 'Hospital',
      options: hospitalOptions,
      disabled: newUserRole !== 'HOSPITAL_ADMIN' && newUserRole !== 'THERAPIST',
      required: true,
      xs: newUserRole === 'HOSPITAL_ADMIN' ? 4 : 3,
    },
  ];

  const therapistFormFields = [
    {
      component: SelectMultipleField,
      fieldKey: 'therapistSpecializations',
      label: 'Therapist Specializations',
      options: specializationRoles,
      required: true,
      disabled: newUserRole !== 'THERAPIST',
      xs: account && account.accountType === ACCOUNT_TYPE.HOS_ADMIN ? 4 : 3,
    },
  ];

  const formFields = useMemo(() => {
    if (newUserRole === 'ADMIN' || newUserRole === undefined) return standardFormFields;
    if (newUserRole === 'ORGANIZATION_ADMIN') return orgAdminFormFields;
    if (newUserRole === 'HOSPITAL_ADMIN') return hospitalAdminFormFields;
    if (account && account.accountType === ACCOUNT_TYPE.HOS_ADMIN) {
      return [...standardFormFields, ...therapistFormFields];
    } else {
      return [...hospitalAdminFormFields, ...therapistFormFields];
    }
  }, [newUserRole, accountRoleOptions]);

  return isFetched ? (
    <FormBuilder
      disabled={disableAll}
      initValues={initValue}
      formFields={formFields}
      onBack={onBack}
      onSubmit={onSubmit}
      onUpdate={update}
      hideSubmit={hideSubmitButtons}
      rowSpacing={10}
      columnSpacing={8}
    />
  ) : null;
};
